import { Injectable, Injector, Optional } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import {MatSnackBar} from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { SentryService } from '../services/sentry.service';
import { SKIP_ERROR_SNACKBAR } from './http-context-tokens';
import { apiErrorText } from './api-error-message';
import { isSessionReplacedError, rememberSignedOutOnOtherDevice } from '../session-signout';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(private router: Router, private spinner: NgxSpinnerService,
     private _snackBar: MatSnackBar, private _http: HttpClient,
     private sentryService: SentryService,
     @Optional() private injector?: Injector) { }

  /** Looked up when an error lands, not in the constructor: TranslateService loads over HttpClient. */
  private get translate(): TranslateService | null {
    try {
      return this.injector?.get(TranslateService, null) ?? null;
    } catch {
      return null;
    }
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request)
      .pipe(catchError((err: any) => {
        if (err instanceof HttpErrorResponse) {
          if ((err.status === 440 || err.status === 401)  && !this.isAuthFreeRoute() && !this.isBillingOnlyRoute(request.url)) {
              this.sentryService.addBreadcrumb({
                category: 'auth',
                message: `User unauthenticated (status: ${err.status})`,
                level: 'warning'
              });
              // Card #364: "signed in on another device" leaves a note for the sign-in page.
              if (isSessionReplacedError(err)) rememberSignedOutOnOtherDevice();
              this.unAuthenticatedUserRemove(); 
          }
          // An expired quote refusing Accept (409 QUOTE_EXPIRED) is rendered by the public
          // quote page as its own banner — don't flash the raw code in a snackbar.
          // Card #349: the same for a call that shows its own message (the public accept, with
          // "Try again"), so no raw code such as ACCEPT_NOT_SAVED is flashed.
          if ((err.status === 409 && err?.error?.message === 'QUOTE_EXPIRED') || request?.context?.get(SKIP_ERROR_SNACKBAR)) {
            // Same spinner release the message-string branch below does, minus the snackbar.
            this.spinner.hide();
            return throwError(() => err);
          }
          // Card #566: a coded API error shows in the user's language (English message as fallback).
          if (err?.error?.error && typeof err?.error?.error === 'string') {
              this._snackBar.open( apiErrorText(err?.error, this.translate), "OK" , {
                panelClass: ['mat-custom-snackbar-class'],
                duration: 2000
              });
          } else if (err?.error && typeof err?.error?.message === 'string') {
            this.spinner.hide();
            this._snackBar.open( apiErrorText(err?.error, this.translate), "OK" , {
              panelClass: ['mat-custom-snackbar-class'],
              duration: 6000
            });
          }  
        }
         return throwError(() => err);
      }));
  }

  private isAuthFreeRoute(): boolean {
    const url = this.router?.url || window?.location?.pathname || '';
    return (
      url.startsWith('/auth') ||
      url.startsWith('/auth2') ||
      url.startsWith('/auth3') ||
      url.includes('/invoice/') ||
      url.includes('/payment/') ||
      url.includes('/lead-form-embed/') ||
      url.includes('/quote-form-embed/') ||
      url.includes('/e-quote-view/') ||
      url.includes('/maintenance') ||
      url.includes('/downloadfile') ||
      url.includes('/equote-ui')
    );
  }

  /**
   * Returns true when a 401 from this URL is a backend permissions error, not a
   * dead session — so the global logout must NOT fire.
   *
   * Since the 2026-09-08 security change, merch-backend incorrectly applies a
   * billing_access guard to routes that all members should reach (e.g.
   * /merchantuserdata/list, /prices/list). Users who lack billing_access in their
   * permission set (e.g. Sales role) receive 401 from all merch-backend calls even
   * though their JWT and Redis session are perfectly valid.
   *
   * Scope: only merch-backend (environment.API_URL). NestJS-service 401s
   * (different hostnames) still trigger logout to catch real expired sessions.
   *
   * Fix required on the backend: narrow the billing_access guard to only the
   * close-account action on /merchant/merchantprofile.
   */
  private isBillingOnlyRoute(url: string): boolean {
    // Only applies to merch-backend requests
    if (!url.startsWith(environment.API_URL)) return false;

    // Check whether the logged-in user has billing_access in their permission list.
    // PermissionService stores the array as JSON in localStorage under 'permission'.
    // A user WITHOUT billing_access is a non-owner role (e.g. Sales): any 401 they
    // get from merch-backend is a backend permissions bug, not a dead session.
    try {
      const raw = localStorage.getItem('permission');
      if (!raw) return false;
      const permissions: string[] = JSON.parse(raw);
      return Array.isArray(permissions) && !permissions.includes('billing_access');
    } catch {
      return false;
    }
  }

  
  

  private unAuthenticatedUserRemove() {
    if (localStorage.getItem('merchant_id')) {
      // this._http.post<any>(`${environment.USER_MANAGEMENT_URL}/merchantuser/removeusersession`, {
      //  id:localStorage.getItem('userId'),
      //  email:localStorage.getItem('email'),
      //  merchant_id:localStorage.getItem('merchant_id'),
      //  device_id:localStorage.getItem('deviceLoginId')
      //  }).subscribe(() => {});

   }
   localStorage.removeItem('reminderList');
   localStorage.removeItem('merchant_id');
   localStorage.removeItem('token');
   localStorage.removeItem('userId');
   localStorage.removeItem('fname');
   localStorage.removeItem('lname');
   localStorage.removeItem('create-session');
   localStorage.removeItem('redirect-merchant');
   localStorage.removeItem('profile_pic');
   localStorage.removeItem('email');
   localStorage.removeItem('user_merchants');
   localStorage.removeItem('deviceLoginId');
   localStorage.removeItem('loginUserValue');
   localStorage.removeItem('osmos_access_level');
   localStorage.removeItem('permission');
  localStorage.removeItem('role');
  localStorage.removeItem('country_list');
  localStorage.removeItem('accessId');
  //  this.translate.use('en');
   localStorage.removeItem('osmos_access_level');
   this.router.navigate(['/auth/sign-in']);
   this.spinner.hide();
   window.location.reload();
  }
}
